import { catchError, firstValueFrom, map, Observable, of, Subject, throwError } from "rxjs";
import { CompanyFontsService } from "src/app/core/services/company-fonts.service";
import { DbService } from "src/app/core/services/db.service";
import { IndexConfigObject } from "src/app/domains/index-config-object";
import { Employee, EmployeeStatus, EmployeeType, EvaCompany, User } from "src/app/domains/internal";
import { EvaCompanyLight, EvaFeatures, toEvaCompanyLight } from "src/app/features/eva-company/domains/eva-company";
import { environment } from "src/environments/environment";

export interface UserContextDto {
  employee: {
    employee_id: string;
    employee_type: string;
    is_superadmin: boolean;
    ai_assistant_enabled: boolean;
    status: string;
  };
  company_features: string[];
  company_offer_workflows_enabled?: boolean; // absent tant que le back n'est pas déployé
  company_status: string;
  companies_count: number;
}

/** La société en cache (localStorage) diffère-t-elle du contexte serveur sur les fonctionnalités ? */
export function companyFeaturesChanged(company: EvaCompany | null | undefined, ctx: UserContextDto): boolean {
  const currentFeatures = [...(company?.features_enabled || [])].sort().join(",");
  const newFeatures = [...(ctx.company_features || [])].sort().join(",");
  return currentFeatures !== newFeatures;
}

/** Réglages société hors fonctionnalités, rechargés sans message (activés par un autre employé). */
export function companySettingsChanged(company: EvaCompany | null | undefined, ctx: UserContextDto): boolean {
  // Champ absent : ancienne API, pas de rechargement en boucle.
  if (ctx.company_offer_workflows_enabled === undefined) return false;
  return !!company?.offer_workflows_enabled !== ctx.company_offer_workflows_enabled;
}

import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { EvaService } from "../../../core";
import { VAT } from "../../../domains/vat";
import { AuthenticationService } from "../../../services/authentication.service";
import { EmployeesService } from "../../employees/services/employees.service";
import { EvaSubscriptionService } from "../../subscription/eva-subscription.service";
import { HrYearlyConfig, RegeneratePeriodClosedPreview } from "../domains/hr-yearly-config";
import { PeriodClosed } from "../domains/period-closed";
import { MealBreak } from "../domains/meal-break";

@Injectable({ providedIn: "root" })
export class EvaCompanyService {
  url = environment.apiUrl + "/eva/companies";
  urlYearlyConfig = environment.apiUrl + "/yearly-configs";
  urlInterventions = environment.apiUrl + "/interventions";
  urlIndexConfig = environment.apiUrl + "/eva/index-config";

  public currentEvaCompany: EvaCompany;
  public currentEvaCompaniesAvailable: EvaCompany[] = [];

  public currentEvaEmployee: Employee;

  private companyChangedSubject = new Subject<{ company: EvaCompany; employee: Employee }>();
  public companyChanged$ = this.companyChangedSubject.asObservable();

  private companyUpdatedSubject = new Subject<EvaCompany>();
  public companyUpdated$ = this.companyUpdatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private employeeService: EmployeesService,
    private authenticationService: AuthenticationService,
    private evaService: EvaService,
    private evaSubscriptionService: EvaSubscriptionService,
    private dbService: DbService,
    private companyFontsService: CompanyFontsService,
  ) {
    this.load();
    this.startContextPolling();
  }

  // ── Polling intelligent (Phase 3) ────────────────────────────────────────

  private startContextPolling(): void {
    setInterval(() => this.pollUserContext(), 300000); // toutes les 5 min
    this.pollUserContext(); // premier poll au démarrage
  }

  private pollUserContext(): void {
    if (!localStorage.getItem("evaJwt") || this.authenticationService.isLoggingOut) return;

    this.http.get<UserContextDto>(`${environment.apiUrl}/auth/user-context`).subscribe({
      next: (ctx) => {
        const currentEmployee = this.getCurrentEvaEmployee();
        const currentCompany = this.getCurrentEvaCompany();

        // Cas 1 : Employee désactivé
        if (ctx.employee.status === "DISABLED") {
          this.authenticationService.logout();
          return;
        }

        // Cas 2 : Rôle ou superadmin changé → reload employee
        const rightsChanged =
          ctx.employee.employee_type !== currentEmployee?.employee_type ||
          ctx.employee.is_superadmin !== currentEmployee?.is_superadmin;
        // Accès à l'assistant IA changé : reload sans message (au premier contrôle après la mise en
        // prod, l'employé en cache n'a pas encore ce champ).
        if (rightsChanged || ctx.employee.ai_assistant_enabled !== currentEmployee?.ai_assistant_enabled) {
          this.employeeService.getCurrentEmployee(false).subscribe((emp) => {
            this.updateEmployee(emp);
            if (rightsChanged) this.evaService.showWarning("Vos droits ont été mis à jour.");
          });
        }

        // Cas 3 : Features ou réglages société changés → reload company courante
        const featuresChanged = companyFeaturesChanged(currentCompany, ctx);
        if ((featuresChanged || companySettingsChanged(currentCompany, ctx)) && currentCompany?.eva_company_id) {
          this.get(currentCompany.eva_company_id).subscribe((company) => {
            // notifyCompanyUpdated émet companyUpdated$ : les écrans ouverts (offres) réévaluent leurs réglages
            this.notifyCompanyUpdated(company);
            if (featuresChanged)
              this.evaService.showWarning("Les fonctionnalités de votre société ont été mises à jour.");
          });
        }

        // Cas 4 : Accès à une société ajouté/retiré → refresh liste
        const currentCount = this.currentEvaCompaniesAvailable?.length ?? 0;
        if (ctx.companies_count !== currentCount) {
          this.getAllLight().subscribe((companies) => {
            this.updateEvaCompanies(companies);
          });
        }

        localStorage.setItem("globalCacheDt", new Date().toISOString());
      },
      error: () => {
        /* silencieux — erreur réseau temporaire */
      },
    });
  }

  getCurrentEvaCompany(): EvaCompany {
    if ("currentEvaCompany" in localStorage) {
      try {
        const raw = this.getFromSessionThenLocalStorage("currentEvaCompany");
        if (raw == null || raw === "undefined") throw new Error("corrupted");
        return EvaCompany.createInstance(JSON.parse(raw));
      } catch {
        localStorage.removeItem("currentEvaCompany");
        sessionStorage.removeItem("currentEvaCompany");
      }
    }
    if (!this.authenticationService.isLoggingOut) {
      this.authenticationService.logout();
      this.router.navigate(["/login"]);
    }
    return new EvaCompany();
  }

  getCurrentEvaCompaniesAvailable(): EvaCompany[] {
    if ("currentEvaCompaniesAvailable" in localStorage) {
      // Stocké en EvaCompanyLight[], reconstruit en EvaCompany[] (champs manquants = valeurs par défaut)
      return EvaCompany.createInstances(JSON.parse(localStorage.getItem("currentEvaCompaniesAvailable")));
    } else {
      if (!this.authenticationService.isLoggingOut) {
        this.authenticationService.logout();
        this.router.navigate(["/login"]);
      }
      return [new EvaCompany()];
    }
  }

  getCurrentEvaEmployee(): Employee {
    if ("currentEvaEmployee" in localStorage) {
      const raw = this.getFromSessionThenLocalStorage("currentEvaEmployee");
      if (!raw || raw === "undefined") {
        localStorage.removeItem("currentEvaEmployee");
        sessionStorage.removeItem("currentEvaEmployee");
        return new Employee();
      }
      return Employee.createInstance(JSON.parse(raw));
    } else {
      if (!this.authenticationService.isLoggingOut) {
        this.authenticationService.logout();
        this.router.navigate(["/login"]);
      }
      return new Employee();
    }
  }

  create(evaCompany: EvaCompany, employee: Employee, user: User) {
    return this.http.post<any>(this.url + "/create-eva-company", {
      evaCompany: evaCompany,
      employee: employee,
      user: user,
    });
  }

  public changeCompany(evaCompanyId: string, redirectToHome = true, refreshOnly = false, redirectPath?: string): void {
    this.evaService.showLoader();

    // Récupère le nom depuis la liste légère (pour le message de succès)
    const companyLight = this.getCurrentEvaCompaniesAvailable().find((x) => x.eva_company_id === evaCompanyId);

    this.evaSubscriptionService.reloadSubscription();

    // Charge la company complète avant de la stocker comme currentEvaCompany
    this.get(evaCompanyId).subscribe({
      next: (fullCompany) => {
        this.updateEvaCompany(fullCompany);
        void this.companyFontsService.load(fullCompany.eva_company_id);

        this.employeeService.getCurrentEmployee(false).subscribe(
          async (employee) => {
            if (employee.status === EmployeeStatus.DISABLED) {
              this.authenticationService.logout();
              this.router.navigate(["/login"]);
            }
            this.updateEmployee(employee);
            await firstValueFrom(this.evaSubscriptionService.reloadSubscription(), { defaultValue: null });
            this.companyChangedSubject.next({ company: fullCompany, employee });
            this.evaService.showSuccess("Désormais connecté sur " + (companyLight?.name ?? fullCompany.name));
            this.evaService.hideLoader();

            if (redirectToHome || redirectPath) {
              // Redirect + full reload pour réinitialiser tout le state Angular
              const target = redirectPath ?? "/home";
              window.location.hash = target;
              window.location.reload();
            } else if (refreshOnly) {
              window.location.reload();
            }
          },
          (error) => {
            this.evaService.showError("Une erreur est survenue lors du changement de société", "changeCompany", error);
            this.evaService.hideLoader();
          },
        );
      },
      error: (error) => {
        this.evaService.showError("Une erreur est survenue lors du changement de société", "changeCompany", error);
        this.evaService.hideLoader();
      },
    });
  }

  redirectTo(uri: string) {
    let path = uri;
    let queryParams: any = {};

    const hasQuery = uri.includes("?");
    if (hasQuery) {
      const [p, q] = uri.split("?");
      path = p || "/";
      queryParams = Object.fromEntries(new URLSearchParams(q));
    }

    this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
      this.router.navigate([path], { queryParams }).then(() => {
        window.location.reload();
      });
    });
  }

  async load(): Promise<void> {
    try {
      if ("currentEvaCompany" in sessionStorage || "currentEvaCompany" in localStorage) {
        this.currentEvaCompany = EvaCompany.createInstance(
          JSON.parse(this.getFromSessionThenLocalStorage("currentEvaCompany")),
        );
        void this.companyFontsService.load(this.currentEvaCompany.eva_company_id);
        // Stocké en EvaCompanyLight[], reconstruit en EvaCompany[] avec valeurs par défaut
        const rawCompanies = localStorage.getItem("currentEvaCompaniesAvailable");
        this.currentEvaCompaniesAvailable =
          rawCompanies && rawCompanies !== "undefined" ? EvaCompany.createInstances(JSON.parse(rawCompanies)) : [];
        this.currentEvaEmployee = JSON.parse(this.getFromSessionThenLocalStorage("currentEvaEmployee"));

        // Nettoyage ancien cache employé (supprimé au profit du cache mémoire)
        sessionStorage.removeItem("employeeMe-");

        // Refresh async du profil employé pour avoir les permissions à jour
        // Différé au tick suivant pour éviter un conflit DI lors du bootstrap Angular (Sentry #7315805400)
        setTimeout(() => {
          this.employeeService.getCurrentEmployee(false).subscribe((employee) => {
            this.updateEmployee(employee);
          });
        }, 0);
        // Synchroniser lors des auto-refresh (toutes les 15 min)
        this.employeeService.onCurrentEmployeeRefresh((employee) => {
          this.updateEmployee(employee);
        });
      } else if ("evaUser" in localStorage) {
        try {
          const evaCompanies = await firstValueFrom(this.getAllLight());
          if (evaCompanies.length > 0) {
            this.updateEvaCompanies(evaCompanies);
            // Charge la company complète (getAllLight retourne des données partielles)
            const fullCompany = await firstValueFrom(this.get(evaCompanies[0].eva_company_id));
            this.updateEvaCompany(fullCompany);
            void this.companyFontsService.load(fullCompany.eva_company_id);
            const employee = await firstValueFrom(this.employeeService.getCurrentEmployee());
            if (employee.status === EmployeeStatus.DISABLED) {
              this.authenticationService.logout();
              this.router.navigate(["/login"]);
            }
            this.updateEmployee(employee);
            this.router.navigate(["/"]);
          }
        } catch (error) {
          console.error(error);
        }
      }
    } catch (error) {
      console.error(error);
      this.authenticationService.logout();
    }
  }

  updateEmployee(employee: Employee) {
    this.currentEvaEmployee = employee;
    this.setSessionAndLocalStorage("currentEvaEmployee", JSON.stringify(this.currentEvaEmployee));
  }

  updateEmployeeAvatar(avatar: string) {
    this.dbService.saveAvatar(avatar);
  }

  updateEvaCompany(evaCompany: EvaCompany) {
    this.currentEvaCompany = evaCompany;
    this.setSessionAndLocalStorage("currentEvaCompany", JSON.stringify(this.currentEvaCompany));
  }

  updateEvaCompanies(evaCompanies: EvaCompany[]) {
    this.currentEvaCompaniesAvailable = evaCompanies;
    // Ne stocke que les champs minimaux pour le sélecteur (~90% de réduction pour admin multi-tenant)
    const lightList: EvaCompanyLight[] = evaCompanies.map(toEvaCompanyLight);
    try {
      localStorage.setItem("currentEvaCompaniesAvailable", JSON.stringify(lightList));
    } catch (e) {
      console.error("localStorage quota exceeded for evaCompanies");
    }
  }

  notifyCompanyUpdated(updated: EvaCompany): void {
    if (this.currentEvaCompany?.eva_company_id === updated.eva_company_id) {
      this.updateEvaCompany(updated);
    }
    const idx = this.currentEvaCompaniesAvailable.findIndex((c) => c.eva_company_id === updated.eva_company_id);
    if (idx !== -1) {
      const next = [...this.currentEvaCompaniesAvailable];
      next[idx] = updated;
      this.updateEvaCompanies(next);
    }
    this.companyUpdatedSubject.next(updated);
  }

  patchCanton(eva_company_id, canton) {
    return this.http.patch<EvaCompany>(this.url + "/admin/" + eva_company_id + "/canton", { canton: canton });
  }

  patchWorkDomains(eva_company_id, work_domains: string[]) {
    return this.http.patch<EvaCompany>(this.url + "/admin/" + eva_company_id + "/work_domains", {
      work_domains: work_domains,
    });
  }

  getAll(): Observable<EvaCompany[]> {
    return this.http.get<EvaCompany[]>(this.url).pipe(
      map(EvaCompany.createInstances),
      catchError((error) => {
        console.error("Error fetching companies:", error);
        return of([]); // Renvoie un tableau vide en cas d'erreur
      }),
    );
  }

  getAllLight(): Observable<EvaCompany[]> {
    return this.http.get<EvaCompany[]>(this.url + "/light").pipe(
      map(EvaCompany.createInstances),
      catchError((error) => {
        console.error("Error fetching companies:", error);
        return of([]); // Renvoie un tableau vide en cas d'erreur
      }),
    );
  }

  get(id): Observable<EvaCompany> {
    return this.http.get<EvaCompany>(this.url + "/" + id).pipe(map(EvaCompany.createInstance));
  }

  add(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http.post<EvaCompany>(this.url, evaCompany).pipe(map(EvaCompany.createInstance));
  }

  edit(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/" + evaCompany.eva_company_id, evaCompany)
      .pipe(map(EvaCompany.createInstance));
  }

  editCompanyInfos(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/company-infos/" + evaCompany.eva_company_id, evaCompany)
      .pipe(map(EvaCompany.createInstance));
  }
  editLogo(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/logo/" + evaCompany.eva_company_id, evaCompany)
      .pipe(map(EvaCompany.createInstance));
  }
  editLocation(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/location" + evaCompany.eva_company_id, evaCompany)
      .pipe(map(EvaCompany.createInstance));
  }

  editApparenceItem(evaCompany: EvaCompany, item: string): Observable<EvaCompany> {
    if (!evaCompany?.eva_company_id) {
      return throwError(() => new Error("Identifiant de société manquant"));
    }
    return this.http
      .put<EvaCompany>(this.url + "/" + evaCompany.eva_company_id + "/apparence-item", {
        evaCompany: evaCompany,
        item: item,
      })
      .pipe(map(EvaCompany.createInstance));
  }

  editDocs(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/doc-infos/" + evaCompany.eva_company_id, evaCompany)
      .pipe(map(EvaCompany.createInstance));
  }

  editPredifinedTimes(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/predifined-times/" + evaCompany.eva_company_id, evaCompany)
      .pipe(map(EvaCompany.createInstance));
  }

  resetPredifinedTimes(eva_company: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/reset-predifined-times", eva_company)
      .pipe(map(EvaCompany.createInstance));
  }

  updateBillingConfig(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/" + evaCompany.eva_company_id + "/billing-config", {
        billing_config: evaCompany.billing_config,
        intervention_config: evaCompany.intervention_config,
      })
      .pipe(map(EvaCompany.createInstance));
  }
  updateBillingConfigOnInvoice(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/" + evaCompany.eva_company_id + "/billing-config-on-invoice", {
        billing_config: evaCompany.billing_config,
      })
      .pipe(map(EvaCompany.createInstance));
  }

  updateBillingConfigAlert(evaCompany: EvaCompany, alertType: string): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/" + evaCompany.eva_company_id + "/billing-config-alert", {
        billing_config: evaCompany.billing_config,
        alert_type: alertType,
      })
      .pipe(map(EvaCompany.createInstance));
  }

  updateExpenseReportConfig(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(
        this.url + "/" + evaCompany.eva_company_id + "/expense-report-config",
        evaCompany.expense_report_config,
      )
      .pipe(map(EvaCompany.createInstance));
  }

  updatePlanningNotificationConfig(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(
        this.url + "/" + evaCompany.eva_company_id + "/planning-notification-config",
        evaCompany.planning_notification_config,
      )
      .pipe(map(EvaCompany.createInstance));
  }

  updateMobileConfig(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/" + evaCompany.eva_company_id + "/mobile-config", evaCompany.mobile_config)
      .pipe(map(EvaCompany.createInstance));
  }

  updateWebActivityButtonsConfig(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(
        this.url + "/" + evaCompany.eva_company_id + "/web-activity-buttons-config",
        evaCompany.web_activity_buttons_config,
      )
      .pipe(map(EvaCompany.createInstance));
  }

  updateEmployeesConfig(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/" + evaCompany.eva_company_id + "/employees-config", evaCompany.employees_config)
      .pipe(map(EvaCompany.createInstance));
  }

  editFeatures(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http.put<EvaCompany>(this.url + "/" + evaCompany.eva_company_id + "/features", {
      features_enabled: evaCompany.features_enabled,
    });
  }

  delete(eva_company_id: string): Observable<any> {
    return this.http.delete(this.url + "/" + eva_company_id);
  }

  postQueryFields(eva_company_id: string, fields: string[]): Observable<any> {
    return this.http.post<any>(this.url + "/" + eva_company_id + "/query_fields", fields);
  }

  //Yearly config

  public getAllYearlyConfigs(): Observable<HrYearlyConfig[]> {
    return this.http.get<HrYearlyConfig[]>(this.urlYearlyConfig).pipe(map(HrYearlyConfig.createInstances));
  }
  public addYearlyConfig(yearConfig: HrYearlyConfig): Observable<HrYearlyConfig> {
    return this.http.post<HrYearlyConfig>(this.urlYearlyConfig, yearConfig).pipe(map(HrYearlyConfig.createInstance));
  }
  public addYearlyConfigAllCompanies(year): Observable<HrYearlyConfig[]> {
    return this.http
      .post<HrYearlyConfig[]>(this.urlYearlyConfig + "/all-companies" + "?year=" + year, {})
      .pipe(map(HrYearlyConfig.createInstances));
  }
  public addYearlyConfigWithYear(year: number): Observable<HrYearlyConfig> {
    return this.http
      .post<HrYearlyConfig>(this.urlYearlyConfig + "/year", year)
      .pipe(map(HrYearlyConfig.createInstance));
  }
  public getYearlyConfig(id: string): Observable<HrYearlyConfig> {
    return this.http.get<HrYearlyConfig>(this.urlYearlyConfig + "/" + id).pipe(map(HrYearlyConfig.createInstance));
  }
  public getYearlyConfigFromYear(year: number): Observable<HrYearlyConfig> {
    return this.http.get<HrYearlyConfig>(this.urlYearlyConfig + "/" + year).pipe(map(HrYearlyConfig.createInstance));
  }
  public getHasDoneCounterYearlyConfigFromYear(year: number): Observable<boolean> {
    return this.http.get<boolean>(this.urlYearlyConfig + "/" + year + "/has-done-yearly-counters").pipe();
  }
  public patchHasDoneCounterYearlyConfigFromYear(year: number): Observable<any> {
    return this.http.patch(this.urlYearlyConfig + "/" + year + "/done-yearly-counters", {}).pipe();
  }
  public updateYearlyConfig(yearConfig: HrYearlyConfig): Observable<HrYearlyConfig> {
    return this.http
      .put<HrYearlyConfig>(this.urlYearlyConfig + "/" + yearConfig.hr_yearly_config_id, yearConfig)
      .pipe(map(HrYearlyConfig.createInstance));
  }

  public addPeriodClosed(period_closed: PeriodClosed, id): Observable<HrYearlyConfig> {
    return this.http
      .post<HrYearlyConfig>(this.urlYearlyConfig + "/period-closed/" + id, period_closed)
      .pipe(map(HrYearlyConfig.createInstance));
  }
  public deletePeriodClosed(id): Observable<HrYearlyConfig> {
    return this.http
      .delete<HrYearlyConfig>(this.urlYearlyConfig + "/period-closed/" + id)
      .pipe(map(HrYearlyConfig.createInstance));
  }
  public updatePeriodClosed(period_closed: PeriodClosed, id): Observable<HrYearlyConfig> {
    return this.http
      .put<HrYearlyConfig>(this.urlYearlyConfig + "/period-closed/" + id, period_closed)
      .pipe(map(HrYearlyConfig.createInstance));
  }

  public getRegeneratePeriodClosedPreview(year: number): Observable<RegeneratePeriodClosedPreview> {
    return this.http.get<RegeneratePeriodClosedPreview>(this.urlYearlyConfig + "/" + year + "/regenerate-preview");
  }

  public regeneratePeriodClosed(year: number): Observable<void> {
    return this.http.post<void>(this.urlYearlyConfig + "/" + year + "/regenerate", {});
  }

  public addVat(vat: VAT): Observable<EvaCompany> {
    return this.http.post<EvaCompany>(this.url + "/vat", vat).pipe(map(EvaCompany.createInstance));
  }

  public deleteVat(id): Observable<EvaCompany> {
    return this.http.delete<EvaCompany>(this.url + "/vat/" + id).pipe(map(EvaCompany.createInstance));
  }

  public updateVat(vat: VAT): Observable<EvaCompany> {
    return this.http.put<EvaCompany>(this.url + "/vat", vat).pipe(map(EvaCompany.createInstance));
  }
  public resetDocInfos(eva_company: EvaCompany): Observable<EvaCompany> {
    return this.http.put<EvaCompany>(this.url + "/reset-docs-infos", eva_company).pipe(map(EvaCompany.createInstance));
  }

  public resetDocInfosByFilter(eva_company: EvaCompany, filter: string): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/reset-docs-infos/" + filter, eva_company)
      .pipe(map(EvaCompany.createInstance));
  }

  public addMealBreak(mealBreak: MealBreak): Observable<EvaCompany> {
    return this.http.post<EvaCompany>(this.url + "/meal-break", mealBreak).pipe(map(EvaCompany.createInstance));
  }

  public deleteMealBreak(meal_break_id): Observable<EvaCompany> {
    return this.http.delete<EvaCompany>(this.url + "/meal-break/" + meal_break_id).pipe(map(EvaCompany.createInstance));
  }

  public updateMealBreak(meal_break_id, mealBreak: MealBreak): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/meal-break/" + meal_break_id, mealBreak)
      .pipe(map(EvaCompany.createInstance));
  }

  public reorderVat(beforeVat, afterVat): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/vat/reorder", {
        beforeVat: beforeVat,
        afterVat: afterVat,
      })
      .pipe(map(EvaCompany.createInstance));
  }

  public hasProdRights(): boolean {
    return [EmployeeType.ADMIN, EmployeeType.OFFICE, EmployeeType.PROD].includes(
      this.getCurrentEvaEmployee().employee_type,
    );
  }

  public hasOfficeRights(): boolean {
    return [EmployeeType.ADMIN, EmployeeType.OFFICE].includes(this.getCurrentEvaEmployee().employee_type);
  }

  public hasAdminRights(): boolean {
    return this.getCurrentEvaEmployee().employee_type == EmployeeType.ADMIN;
  }

  public hasInvoiceRights(): boolean {
    if (
      this.getCurrentEvaEmployee().employee_type == EmployeeType.OFFICE &&
      this.getCurrentEvaEmployee().employee_position_current.office_allow_project_sensitive_data
    ) {
      return true;
    }
    if ([EmployeeType.ACCOUNTANT, EmployeeType.ADMIN].includes(this.getCurrentEvaEmployee().employee_type)) {
      return true;
    }
    return false;
  }

  public hasAccountantRights(): boolean {
    return this.getCurrentEvaEmployee().employee_type == EmployeeType.ACCOUNTANT || this.hasAdminRights();
  }

  public hasSuperAdminRights(): boolean {
    return this.getCurrentEvaEmployee().is_superadmin;
  }

  /** Feature « Assistant IA » de la société : ouvre la gestion des accès aux admins. */
  public hasAiAssistantFeature(): boolean {
    return !!this.currentEvaCompany?.hasFeature(EvaFeatures.AI_ASSISTANT);
  }

  /** Même règle qu'EVA-BACK (pairing_request_service.can_use_agent) : feature et accès de la personne. */
  public canUseAiAssistant(): boolean {
    return this.hasAiAssistantFeature() && !!this.getCurrentEvaEmployee()?.ai_assistant_enabled;
  }

  getFromSessionThenLocalStorage(key: string): any {
    let value = sessionStorage.getItem(key);
    if (value == null) {
      value = localStorage.getItem(key);
    }
    return value;
  }

  setSessionAndLocalStorage(key: string, value: any): void {
    sessionStorage.setItem(key, value);
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error("localStorage quota exceeded for", key);
    }
  }

  patchIsShowsOfferV2(isShowsOffersV2: boolean): Observable<EvaCompany> {
    return this.http
      .patch<EvaCompany>(this.url + "/isShowsOffersV2", { isShowsOffersV2: isShowsOffersV2 })
      .pipe(map(EvaCompany.createInstance));
  }

  patchIsShowMealBreaks(is_meal_break_allowed: boolean): Observable<EvaCompany> {
    return this.http
      .patch<EvaCompany>(this.url + "/is_meal_break_allowed", { is_meal_break_allowed: is_meal_break_allowed })
      .pipe(map(EvaCompany.createInstance));
  }

  patchIsOtherActivityEnabled(value: boolean): Observable<EvaCompany> {
    return this.http
      .patch<EvaCompany>(this.url + "/is_other_activity_enabled", { is_other_activity_enabled: value })
      .pipe(map(EvaCompany.createInstance));
  }

  public async refreshEvaCompanies() {
    if (!localStorage.getItem("evaJwt") || this.authenticationService.isLoggingOut) {
      return;
    }

    const currentEvaCompany = this.getCurrentEvaCompany();
    if (!currentEvaCompany) {
      return;
    }

    localStorage.setItem("globalCacheDt", new Date().toISOString());

    // Utilise getAllLight() au lieu de getAll() pour économiser la bande passante
    const companies = await firstValueFrom(this.getAllLight());
    if (!Array.isArray(companies)) {
      console.error("Expected an array but got:", companies);
      return;
    }
    this.updateEvaCompanies(companies);

    const refreshedEvaCompany = await firstValueFrom(this.get(currentEvaCompany.eva_company_id));
    this.updateEvaCompany(refreshedEvaCompany);

    const employee = await firstValueFrom(this.employeeService.getCurrentEmployee(false));
    this.updateEmployee(employee);

    const avatar = await firstValueFrom(this.employeeService.getAvatar(employee.employee_id));
    this.updateEmployeeAvatar(avatar);
  }

  adminPatchCompany(eva_company_id: string, body: any): Observable<any> {
    return this.http.patch(this.url + "/admin/" + eva_company_id, body);
  }

  patchCompany(eva_company_id: string, body: any): Observable<any> {
    return this.http.patch(this.url + "/" + eva_company_id, body);
  }

  adminDeleteCompany(eva_company_id: any) {
    return this.http.delete(this.url + "/admin/" + eva_company_id);
  }

  editChiftAccountingNumber(evaCompany: EvaCompany, chift_account: string): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/chift-accounting-number/" + evaCompany.eva_company_id, {
        evaCompany: evaCompany,
        chift_account: chift_account,
      })
      .pipe(map(EvaCompany.createInstance));
  }

  editAccountingCodes(evaCompany: EvaCompany): Observable<EvaCompany> {
    return this.http
      .put<EvaCompany>(this.url + "/accounting-codes/" + evaCompany.eva_company_id, {
        evaCompany: evaCompany,
      })
      .pipe(map(EvaCompany.createInstance));
  }

  // ID CONFIG

  getIdConfig(objectType): Observable<IndexConfigObject> {
    return this.http
      .get<IndexConfigObject>(this.urlIndexConfig + "/" + objectType)
      .pipe(map(IndexConfigObject.createInstance));
  }
  getAllIdConfig(): Observable<IndexConfigObject[]> {
    return this.http
      .get<IndexConfigObject[]>(this.urlIndexConfig + "/all")
      .pipe(map(IndexConfigObject.createInstances));
  }

  updateIdConfig(indexConfigId, indexConfig): Observable<IndexConfigObject> {
    return this.http
      .put<IndexConfigObject>(this.urlIndexConfig + "/" + indexConfigId, indexConfig)
      .pipe(map(IndexConfigObject.createInstance));
  }

  getNextCounterForConfig(objectType): Observable<number> {
    return this.http.get<number>(this.urlIndexConfig + "/" + objectType + "/next-counter").pipe();
  }

  regenIdForObject(objectType, objectId): Observable<string> {
    return this.http.get<string>(this.urlIndexConfig + "/" + objectType + "/set-id/" + objectId).pipe();
  }
}
